<template>
  <div class="clickable-offers-table">
    <mf-card-container
      v-model="delaySearch"
      :title="`Demais ofertas ${type === 'ativaveis' ? 'ativáveis' : 'livres'} em exibição`"
      description="São exibidas aqui ofertas ativas ou agendadas que foram configuradas da mesma maneira para todas as lojas selecionadas"
      placeholder="Buscar produto"
      use-search
    >
      <template v-slot:filters>
        <filters-menu-v2 :filters-quantity="filtersQuantity" @apply-filters="applyFilters" @clear-filters="clearFilters">
          <v-container class="px-0 py-0">
            <v-row>
              <v-col>
                <v-select
                  v-model="filters.targetAudience"
                  :items="[
                    { text: 'Personalizada', val: 'personalized' },
                    { text: 'Geral', val: 'general' },
                    { text: 'Clube', val: 'club' },
                    { text: 'Todos', val: 'Todos' },
                  ]"
                  label="Público"
                  hide-details
                  outlined
                  item-value="val"
                  item-text="text"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-select
                  v-model="filters.active"
                  :items="[
                    { text: 'Agendado', val: false },
                    { text: 'Ativo', val: true },
                    { text: 'Todos', val: 'Todos' },
                  ]"
                  label="Status"
                  hide-details
                  outlined
                  item-value="val"
                  item-text="text"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-select
                  v-model="filters.hasImage"
                  :items="[
                    { text: 'Sim', val: true },
                    { text: 'Não', val: false },
                    { text: 'Todos', val: 'Todos' },
                  ]"
                  label="Com imagem"
                  hide-details
                  outlined
                  item-value="val"
                  item-text="text"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col class="px-4 py-0">
                <span class="hint-text">&nbsp;&nbsp;Os filtros já foram aplicados, mas deixamos o botão de aplicar pra você se sentir mais seguro.</span>
              </v-col>
            </v-row>
          </v-container>
        </filters-menu-v2>
      </template>
      <v-data-table
        :headers="headers"
        :loading="$apollo.queries.salesProducts.loading"
        :server-items-length="records"
        :items="items"
        :options.sync="options"
        :footer-props="{ itemsPerPageOptions: [5, 10, 15] }"
        class="table-add-offers"
        ondragstart="return false"
        ondrop="return false"
      >
        <template v-slot:item="{ item }">
          <tr :class="item.currentAdded ? 'added-item' : 'clickable-item'" @click="selectItem(item)">
            <td :class="!item.image_url ? 'icon-align' : ''">
              <v-img v-if="item.image_url" :src="item.image_url" :class="item.currentAdded ? 'td-img disabled-img' : 'td-img'" />
              <mf-icon v-else class="td-img">image_not_supported</mf-icon>
            </td>
            <td>
              <a v-if="item.sku" class="link" target="_blank" :href="linkProduct(item.sku)">{{ item.sku }}</a>
              <span v-else>-</span>
            </td>
            <td>{{ item.name }}</td>
            <td>
              {{ getProductTypeLabel(item.personalized) }}
            </td>
            <td>
              <div class="d-flex align-center">
                <date-time-status-colors :start-date="item.start_date" :end-date="item.end_date" />
                <div>
                  {{ validityDates(item.start_date, item.end_date) }}
                </div>
              </div>
            </td>
            <td>
              {{ getProductFormatTypeLabel(item.type) }}
            </td>
            <td>
              <span v-if="item.discount_percentage">{{ formatPercent(item.discount_percentage) }}</span>
              <span v-else>-</span>
            </td>
            <td v-if="$getAllowedModules()['cashback']">
              <span v-if="item.cashback_absolute">{{ item.cashback_absolute | localeString(2) | currency }}</span>
              <span v-else-if="item.cashback_percentage">{{ item.cashback_percentage | localeString(0) | percent }}</span>
              <span v-else>-</span>
            </td>
            <td v-if="$getAllowedModules()['points']">
              <span v-if="item.additional_points_rate">{{ item.additional_points_rate | localeString(0) }}%</span>
              <span v-else>-</span>
            </td>
          </tr>
        </template>
        <template slot="no-data">
          <span>Nenhuma oferta {{ type === 'ativaveis' ? 'ativável' : 'livre' }} disponível para essa combinação de filtros e lojas</span>
        </template>
      </v-data-table>
    </mf-card-container>
  </div>
</template>

<script>
import { SORTABLE_SALE_PRODUCTS } from '../../graphql'
import { floatToPercentString } from '@/config/helpers.js'
import { getProductTypeLabel, getProductFormatTypeLabel } from '@/modules/mobile/ofertas/helpers/productType.js'
import { DateTime } from 'luxon'

export default {
  components: {
    FiltersMenuV2: () => import('@/components/FiltersMenuV2.vue'),
    DateTimeStatusColors: () => import('./DateTimeStatusColors.vue'),
  },
  props: {
    type: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    clickedItem: null,
    options: {},
    filters: {
      targetAudience: 'Todos',
      active: 'Todos',
      hasImage: 'Todos',
    },
    filtersQuantity: 0,
    searchItem: '',
    delaySearch: null,
    timeout: null,
  }),
  computed: {
    highlightedOffers() {
      return this.$store.getters.highlightedOffers
    },
    records() {
      return this.salesProducts?.records || 0
    },
    items: {
      get() {
        return this.salesProducts?.salesProducts || []
      },
      set(val) {
        this.salesProducts.salesProducts = val
      },
    },
    unitsIds() {
      return this.$store.getters.selectedUnits.map((unit) => unit.id_erp)
    },
    headers() {
      const modules = this.$getAllowedModules()
      const cashbackAndPointsAreNotActive = !modules.cashback && !modules.points

      let headers = [
        {
          text: 'Imagem',
          value: 'image_url',
          width: '8%',
        },
        {
          text: 'Código',
          value: 'sku',
          width: '8%',
        },
        {
          text: 'Nome em exibição',
          value: 'name',
          width: cashbackAndPointsAreNotActive ? '27%' : '21%',
        },
        {
          text: 'Tipo',
          value: 'personalized',
          width: '11%',
        },
        {
          text: 'Validade',
          value: 'start_date',
          width: cashbackAndPointsAreNotActive ? '22%' : '18%',
        },
        {
          text: 'Formato',
          value: 'type',
          width: cashbackAndPointsAreNotActive ? '12%' : '11%',
        },
        {
          text: 'Desconto',
          value: 'discount_percentage',
          width: cashbackAndPointsAreNotActive ? '11%' : '10%',
        },
        ...(modules.cashback
          ? [
              {
                text: 'Cashback',
                value: 'cashback_absolute',
                width: '13%',
              },
            ]
          : []),
        ...(modules.points
          ? [
              {
                text: 'Pontos ext.',
                value: 'additional_points_rate',
                width: '13%',
              },
            ]
          : []),
      ]
      return headers
    },
    isFranchisee() {
      return this.$getLoggedUser().is_franchisee
    },
  },
  apollo: {
    salesProducts: {
      query: SORTABLE_SALE_PRODUCTS,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          page: this.options.page || 1,
          pageSize: this.options.itemsPerPage || 10,
          search: this.searchItem,
          sort: this.options.sortBy,
          sort_direction: this.options.sortDesc && this.options.sortDesc.map((direction) => (direction ? 'DESC' : 'ASC')),
          app_units: this.unitsIds && Boolean(this.unitsIds.length) ? this.unitsIds : undefined,
          activatable: this.type === 'ativaveis',
          active: this.filters.active === 'Todos' ? undefined : this.filters.active,
          target_audience: this.filters.targetAudience === 'Todos' ? undefined : this.filters.targetAudience,
          has_image: this.filters.hasImage === 'Todos' ? undefined : this.filters.hasImage,
          grouped_sales_discount: true,
          ...(this.isFranchisee && { franchise: this.isFranchisee }),
        }
      },
      update({ salesProducts }) {
        if (salesProducts) {
          salesProducts.salesProducts = salesProducts.salesProducts.map((item) => {
            return {
              ...item,
              currentAdded: this.highlightedOffers.map((offer) => offer._id).includes(item._id),
            }
          })
        }
        return salesProducts
      },
    },
  },
  watch: {
    delaySearch(value) {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.searchItem = value
      }, 500)
    },
    searchItem() {
      this.options.page = 1
    },
    filters: {
      handler(filters) {
        this.options.page = 1
        this.filtersQuantity = 0
        if (filters.targetAudience !== 'Todos') {
          this.filtersQuantity++
        }

        if (filters.active !== 'Todos') {
          this.filtersQuantity++
        }

        if (filters.hasImage !== 'Todos') {
          this.filtersQuantity++
        }
      },
      deep: true,
    },
    highlightedOffers: {
      handler(newVal, oldVal) {
        if (newVal && oldVal && newVal.length === oldVal.length) return
        this.items.forEach((item) => {
          if (newVal.map((offer) => offer._id).includes(item._id)) {
            item.currentAdded = true
          } else {
            item.currentAdded = false
          }
        })
      },
      deep: true,
    },
  },
  methods: {
    getProductTypeLabel,
    getProductFormatTypeLabel,
    formatPercent(value) {
      return floatToPercentString(value)
    },
    linkProduct(param) {
      return `#/produtos/detalhes-produto/${param}`
    },
    selectItem(item) {
      if (item.currentAdded) return
      this.$store.dispatch('addHighlightedOffer', item)
    },
    validityDates(startDate, endDate) {
      return `${DateTime.fromISO(startDate).toFormat('dd/MM/yyyy')} - ${DateTime.fromISO(endDate).toFormat('dd/MM/yyyy')}`
    },
    applyFilters() {
      this.$apollo.queries.salesProducts.refetch()
    },
    clearFilters() {
      this.filters.targetAudience = 'Todos'
      this.filters.active = 'Todos'
      this.filters.hasImage = 'Todos'
      this.filtersQuantity = 0
    },
  },
}
</script>

<style lang="scss">
.clickable-offers-table {
  .table-header-description {
    max-width: 650px;
    margin-top: -8px !important;
  }

  td {
    @extend .texto-corrido;
  }

  .table-add-offers {
    th {
      @extend .column-title;
      font-weight: 700;
    }
  }

  .added-item {
    cursor: auto;
    td {
      color: $cinza-l50;
    }
  }

  .clickable-item {
    cursor: pointer;
  }

  .icon-align {
    padding-left: 24px;
  }

  .td-img {
    width: 40px;
    height: auto;
  }

  .disabled-img {
    opacity: 0.3;
  }
}
</style>
